import { Controller } from "@hotwired/stimulus"
import { stream as durableStream } from "@durable-streams/client"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = [
    "transcriptDisplay", "transcriptContent", "transcriptLabel", "saveButton", "deleteButton",
    "fileInput", "fileName", "uploadStatus", "fileError",
    "uploadArea", "pasteArea", "pasteInput",
    "uploadModeBtn", "pasteModeBtn", "inputSection"
  ]

  static FILE_LIMITS = {
    text: { extensions: [".md", ".txt"], maxBytes: 5 * 1024 * 1024, label: "5 MB" },
    document: { extensions: [".docx", ".pdf"], maxBytes: 20 * 1024 * 1024, label: "20 MB" },
    audio: { extensions: [".mp3", ".wav"], maxBytes: 100 * 1024 * 1024, label: "100 MB" }
  }
  static values = {
    activityId: String,
    inloopId: String,
    conversionStreamUrl: String
  }

  connect() {
    this.mode = "upload"
    this.conversionAbort = null
    this.processing = false

    // Reconnect to conversion stream on page refresh
    if (this.hasConversionStreamUrlValue && this.conversionStreamUrlValue) {
      this.setProcessingUI()
      this.connectToConversionStream(this.conversionStreamUrlValue)
    }
  }

  disconnect() {
    if (this.conversionAbort) {
      this.conversionAbort.abort()
      this.conversionAbort = null
    }
  }

  // Mode switching
  switchToUpload() {
    this.mode = "upload"
    this.uploadAreaTarget.classList.remove("hidden")
    this.pasteAreaTarget.classList.add("hidden")
    this.uploadModeBtnTarget.className = "px-3 py-1 text-xs font-medium rounded-md transition-colors bg-white text-gray-900 shadow-sm"
    this.pasteModeBtnTarget.className = "px-3 py-1 text-xs font-medium rounded-md transition-colors text-gray-500 hover:text-gray-700"
  }

  switchToPaste() {
    this.mode = "paste"
    this.uploadAreaTarget.classList.add("hidden")
    this.pasteAreaTarget.classList.remove("hidden")
    this.pasteModeBtnTarget.className = "px-3 py-1 text-xs font-medium rounded-md transition-colors bg-white text-gray-900 shadow-sm"
    this.uploadModeBtnTarget.className = "px-3 py-1 text-xs font-medium rounded-md transition-colors text-gray-500 hover:text-gray-700"
  }

  // Handle file selection
  selectFile() {
    if (this.processing) return
    this.fileInputTarget.click()
  }

  fileSelected() {
    const file = this.fileInputTarget.files[0]
    if (!file) return

    const error = this.validateFile(file)
    if (error) {
      this.showFileError(error)
      this.selectedFile = null
      this.fileNameTarget.classList.add("hidden")
      if (this.hasUploadStatusTarget) this.uploadStatusTarget.classList.add("hidden")
      return
    }

    this.clearFileError()
    this.selectedFile = file
    this.fileNameTarget.textContent = file.name
    this.fileNameTarget.classList.remove("hidden")

    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.textContent = "Ready to upload"
      this.uploadStatusTarget.classList.remove("hidden")
    }
  }

  validateFile(file) {
    const ext = ("." + file.name.split(".").pop()).toLowerCase()
    const limits = this.constructor.FILE_LIMITS

    for (const [, limit] of Object.entries(limits)) {
      if (limit.extensions.includes(ext)) {
        if (file.size > limit.maxBytes) {
          return `${file.name} is too large (${this.formatBytes(file.size)}). ${ext.toUpperCase().slice(1)} files must be ${limit.label} or smaller.`
        }
        return null
      }
    }

    const allExtensions = Object.values(limits).flatMap(l => l.extensions).map(e => e.slice(1).toUpperCase()).join(", ")
    return `Unsupported file type "${ext}". Accepted formats: ${allExtensions}.`
  }

  formatBytes(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB"
    if (bytes >= 1024) return (bytes / 1024).toFixed(0) + " KB"
    return bytes + " B"
  }

  showFileError(message) {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = message
    this.fileErrorTarget.classList.remove("hidden")
  }

  clearFileError() {
    if (!this.hasFileErrorTarget) return
    this.fileErrorTarget.textContent = ""
    this.fileErrorTarget.classList.add("hidden")
  }

  // Drag and drop
  dragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("border-brand-500", "bg-brand-50")
  }

  dragLeave(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("border-brand-500", "bg-brand-50")
  }

  drop(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("border-brand-500", "bg-brand-50")
    if (this.processing) return

    const file = e.dataTransfer.files[0]
    if (!file) return

    const error = this.validateFile(file)
    if (error) {
      this.showFileError(error)
      this.selectedFile = null
      this.fileNameTarget.classList.add("hidden")
      if (this.hasUploadStatusTarget) this.uploadStatusTarget.classList.add("hidden")
      return
    }

    this.clearFileError()
    this.selectedFile = file
    this.fileNameTarget.textContent = file.name
    this.fileNameTarget.classList.remove("hidden")

    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.textContent = "Ready to upload"
      this.uploadStatusTarget.classList.remove("hidden")
    }
  }

  // Delete uploaded transcript
  async deleteTranscript() {
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.disabled = true
    }

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/delete_transcript`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          }
        }
      )

      if (response.ok) {
        // Close the modal
        const modal = this.deleteButtonTarget?.closest("[data-controller~='modal']")
        if (modal) {
          const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
          modalController?.close()
        }

        if (this.hasTranscriptDisplayTarget) {
          this.transcriptDisplayTarget.remove()
        }
        // Show input section again
        if (this.hasInputSectionTarget) {
          this.inputSectionTarget.classList.remove("hidden")
        }
        // Reset state
        this.selectedFile = null
        if (this.hasFileNameTarget) this.fileNameTarget.classList.add("hidden")
        if (this.hasUploadStatusTarget) this.uploadStatusTarget.classList.add("hidden")
        if (this.hasPasteInputTarget) this.pasteInputTarget.value = ""
      }
    } catch (error) {
      console.error("Failed to delete transcript:", error)
      if (this.hasDeleteButtonTarget) {
        this.deleteButtonTarget.disabled = false
      }
    }
  }

  // Save transcript
  async save() {
    if (this.processing) return
    if (this.mode === "paste") {
      return this.savePaste()
    }
    return this.saveUpload()
  }

  // Save pasted text
  async savePaste() {
    const content = this.pasteInputTarget.value.trim()
    if (!content) return

    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Saving..."

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/save_transcript`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ content })
        }
      )

      if (response.ok) {
        const data = await response.json()
        this.handleSaveSuccess(data)
      } else {
        const err = await response.json().catch(() => ({}))
        this.saveButtonTarget.textContent = err.error || "Error - Retry"
        this.saveButtonTarget.disabled = false
      }
    } catch (error) {
      console.error("Failed to save transcript:", error)
      this.saveButtonTarget.textContent = "Error - Retry"
      this.saveButtonTarget.disabled = false
    }
  }

  // Save uploaded file
  async saveUpload() {
    if (!this.selectedFile) return

    this.clearFileError()
    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Uploading..."

    try {
      const formData = new FormData()
      formData.append("file", this.selectedFile)

      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/save_transcript`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        }
      )

      if (response.ok) {
        const data = await response.json()

        if (data.converting && data.stream_url) {
          // Async processing (audio STT or docx conversion) — keep UI disabled
          this.setProcessingUI()
          if (this.hasUploadStatusTarget) {
            this.uploadStatusTarget.textContent = "Processing file..."
            this.uploadStatusTarget.classList.remove("hidden")
          }
          this.connectToConversionStream(data.stream_url)
        } else {
          this.handleSaveSuccess(data)
        }
      } else {
        const err = await response.json().catch(() => ({}))
        const message = err.error || "Upload failed. Please check the file and try again."
        this.showFileError(message)
        this.saveButtonTarget.textContent = "Save Transcript"
        this.saveButtonTarget.disabled = false
      }
    } catch (error) {
      console.error("Failed to save transcript:", error)
      this.showFileError("Upload failed. Please check your connection and try again.")
      this.saveButtonTarget.textContent = "Save Transcript"
      this.saveButtonTarget.disabled = false
    }
  }

  handleSaveSuccess(data) {
    this.saveButtonTarget.textContent = "Saved"

    if (this.hasTranscriptContentTarget) {
      const pre = this.transcriptContentTarget.querySelector("pre")
      if (pre) pre.textContent = data.transcript?.content || ""
    }
    if (this.hasTranscriptDisplayTarget) {
      this.transcriptDisplayTarget.classList.remove("hidden")
    }

    this.dispatch("saved", { detail: { content: data.transcript?.content } })

    // Refresh page so the Outcomes tab renders with Generate buttons
    Turbo.visit(window.location.href, { action: "replace" })
  }

  // Connect to durable stream for .docx conversion tracking
  async connectToConversionStream(streamUrl) {
    if (this.conversionAbort) {
      this.conversionAbort.abort()
    }

    this.conversionAbort = new AbortController()

    try {
      const res = await durableStream({
        url: streamUrl,
        offset: "-1",
        live: "sse",
        json: true,
        signal: this.conversionAbort.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.handleConversionEvent(item)
        }
      })

      res.closed.then(() => {
        this.conversionAbort = null
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[call-transcript] Conversion stream error:", error)
      this.conversionAbort = null
    }
  }

  handleConversionEvent(event) {
    switch (event.type) {
      case "progress":
        if (this.hasUploadStatusTarget) {
          this.uploadStatusTarget.textContent = event.message || "Processing..."
          this.uploadStatusTarget.classList.remove("hidden")
        }
        break
      case "completed":
        this.resetProcessingUI()
        if (this.hasTranscriptContentTarget) {
          const pre = this.transcriptContentTarget.querySelector("pre")
          if (pre) pre.textContent = event.content
        }
        if (this.hasTranscriptLabelTarget) {
          this.transcriptLabelTarget.textContent = "Transcript saved"
        }
        if (this.hasTranscriptDisplayTarget) {
          this.transcriptDisplayTarget.classList.remove("hidden")
        }
        if (this.hasInputSectionTarget) {
          this.inputSectionTarget.classList.add("hidden")
        }
        this.dispatch("saved", { detail: { content: event.content } })

        // Refresh page so the Outcomes tab renders with Generate buttons
        Turbo.visit(window.location.href, { action: "replace" })
        break
      case "error":
        this.resetProcessingUI()
        if (this.hasUploadStatusTarget) {
          this.uploadStatusTarget.textContent = `Conversion failed: ${event.error || "Unknown error"}`
          this.uploadStatusTarget.classList.remove("hidden")
        }
        break
    }
  }

  setProcessingUI() {
    this.processing = true
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.textContent = "Processing..."
    }
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.add("opacity-50", "pointer-events-none")
    }
    if (this.hasUploadModeBtnTarget) this.uploadModeBtnTarget.disabled = true
    if (this.hasPasteModeBtnTarget) this.pasteModeBtnTarget.disabled = true
    if (this.hasPasteInputTarget) this.pasteInputTarget.disabled = true
  }

  resetProcessingUI() {
    this.processing = false
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.textContent = "Save Transcript"
    }
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.remove("opacity-50", "pointer-events-none")
    }
    if (this.hasUploadModeBtnTarget) this.uploadModeBtnTarget.disabled = false
    if (this.hasPasteModeBtnTarget) this.pasteModeBtnTarget.disabled = false
    if (this.hasPasteInputTarget) this.pasteInputTarget.disabled = false
  }

  // Save call type and refresh page to update output tab with new output types
  async saveCallType(e) {
    const callType = e.target.value

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/save_configuration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            configuration: { call_type: callType }
          })
        }
      )

      if (response.ok) {
        // Reload page to refresh output tab with call type-specific output types
        Turbo.visit(window.location.href, { action: "replace" })
      }
    } catch (error) {
      console.error("Failed to save call type:", error)
    }
  }
}
